import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  EnvironmentProviders,
  ErrorHandler,
  inject,
  Injectable,
  makeEnvironmentProviders,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';

import { first, firstValueFrom, from, map, switchMap, take, timer } from 'rxjs';

import { addDoc, collection, getDocs, getFirestore, limit, query, serverTimestamp, where } from 'firebase/firestore';
import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAdditionalUserInfo,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  User as FirebaseUser,
} from 'firebase/auth';

import { DialogService } from '@rent2end/core/dialog';
import { FIREBASE_APP } from '@rent2end/core/firebase';
import { SnackbarService } from '@rent2end/core/snackbar';
import { TranslationStore } from '@rent2end/core/translation';

import { AUTH_ADAPTER } from '../../auth-adapter.token';
import { AuthAdapter } from '../../auth-adapter.interface';
import { User } from '../../models/user.model';
import { WEB_APP_API_BASE_URL } from '../../web-app-api-base-url.token';

const EMAIL_ALREADY_VERIFIED_REASON = 'email-already-verified';
const VERIFICATION_EMAIL_RESEND_COOLDOWN_MS = 30_000;

@Injectable()
class FirebaseAuthAdapter implements AuthAdapter {
  private dialogService = inject(DialogService);
  private errorHandler = inject(ErrorHandler);
  private firebaseApp = inject(FIREBASE_APP);
  private httpClient = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private snackBarConfig = inject(MAT_SNACK_BAR_DEFAULT_OPTIONS);
  private snackbarService = inject(SnackbarService);
  private translationStore = inject(TranslationStore);
  private webAppApiBaseUrl = inject(WEB_APP_API_BASE_URL);

  private auth = getAuth(this.firebaseApp);
  private dataProcessingUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/umowa-powierzenia-danych/`);
  private deferUnverifiedSignOut = false;
  private handlingVerificationEmail = false;
  private unverifiedSignOutTimer: ReturnType<typeof setTimeout> | null = null;
  private verificationEmailResendAvailableAt = 0;
  private firestore = getFirestore(this.firebaseApp);
  private pendingRefresh: Promise<string | null> | null = null;
  private privacyPolicyUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/polityka-prywatnosci/`);
  private termsUrl = computed(() => `https://rent2end.pl/${this.translationStore.currentLanguage()}/regulamin/`);

  authState = signal<{ user: User | null | undefined; authToken: string | null }>({ user: undefined, authToken: null });

  constructor() {
    onAuthStateChanged(
      this.auth,
      firebaseUser => {
        const user = firebaseUser?.emailVerified ? this.transformUserToAppModel(firebaseUser) : null;

        this.authState.set({ user, authToken: null });

        if (firebaseUser) {
          if (isPlatformBrowser(this.platformId)) {
            if (!firebaseUser.emailVerified && !this.deferUnverifiedSignOut && !this.handlingVerificationEmail) {
              this.promptVerificationEmailResend(firebaseUser);
            } else if (firebaseUser.emailVerified) {
              this.waitForClaimsAndStoreToken(firebaseUser);
            }
          } else {
            this.storeToken(firebaseUser);
          }
        }
      },
      () => {
        this.authState.set({ user: null, authToken: null });
      }
    );

    onIdTokenChanged(this.auth, (firebaseUser) => {
      if (firebaseUser && this.authState().authToken) {
        firebaseUser.getIdToken().then(token =>
          this.authState.update(state => ({ ...state, authToken: token }))
        );
      }
    });
  }

  async createUser(email: string, password: string, fullName: string, marketingConsent: boolean): Promise<void> {
    this.deferUnverifiedSignOut = true;

    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      this.verificationEmailResendAvailableAt = Date.now() + VERIFICATION_EMAIL_RESEND_COOLDOWN_MS;
      await this.logTermsAcceptance(userCredential.user.uid);

      if (marketingConsent) {
        await this.logMarketingConsent(userCredential.user.uid);
      }

      await updateProfile(userCredential.user, { displayName: fullName });

      this.snackbarService.open(this.translationStore.translate('AUTH.MESSAGES.VERIFY_YOUR_EMAIL'));
    } finally {
      this.cancelUnverifiedSignOut();
      const currentUser = this.auth.currentUser;
      
      if (currentUser && !currentUser.emailVerified) {
        await signOut(this.auth);
      }
      
      this.deferUnverifiedSignOut = false;
    }
  }

  async signInWithEmailAndPassword(email: string, password: string): Promise<User> {
    const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
    return this.transformUserToAppModel(userCredential.user);
  }

  async signInWithFacebook(): Promise<void> {
    await signInWithPopup(this.auth, new FacebookAuthProvider())
      .catch(error => this.errorHandler.handleError(error));
  }

  async signInWithGoogle(marketingConsent: boolean): Promise<User> {
    const userCredential = await signInWithPopup(this.auth, new GoogleAuthProvider());
    const additionalInfo = getAdditionalUserInfo(userCredential);

    if (marketingConsent) {
      await this.logMarketingConsent(userCredential.user.uid);
    }

    const termsAcceptedQuerySnapshot = await getDocs(
      query(
        collection(this.firestore, 'audit_logs'),
        where('uid', '==', userCredential.user.uid),
        where('action', '==', 'terms_accepted'),
        limit(1),
      ),
    );

    let termsAccepted = !termsAcceptedQuerySnapshot.empty;

    if (additionalInfo?.isNewUser || !termsAccepted) {
      termsAccepted = await firstValueFrom(
        this.dialogService
          .openConfirmationDialog({
            confirmationButtonLabel: this.translationStore.translate('COMMON.ACTIONS.AGREE'),
            headline: this.translationStore.translate('AUTH.TERMS_CONFIRMATION_DIALOG.HEADLINE'),
            icon: 'gavel',
            supportingText: this.translationStore.translate(
              'AUTH.TERMS_CONFIRMATION_DIALOG.SUPPORTING_TEXT',
              {
                dataProcessingUrl: this.dataProcessingUrl(),
                privacyPolicyUrl: this.privacyPolicyUrl(),
                termsUrl: this.termsUrl(),
              },
            ),
          })
          .afterClosed()
          .pipe(map(Boolean)),
      );

      if (termsAccepted) {
        await this.logTermsAcceptance(userCredential.user.uid);
        return this.transformUserToAppModel(userCredential.user);
      } else {
        this.signOut();
        throw new Error(this.translationStore.translate('AUTH.MESSAGES.USER_AGREEMENT_NOT_ACCEPTED'));
      }
    } else {
      return this.transformUserToAppModel(userCredential.user);
    }
  }

  async refreshToken(): Promise<string | null> {
    if (this.pendingRefresh) return this.pendingRefresh;

    this.pendingRefresh = this.doRefreshToken();
    try {
      return await this.pendingRefresh;
    } finally {
      this.pendingRefresh = null;
    }
  }

  signOut(): void {
    this.cancelUnverifiedSignOut();
    signOut(this.auth);
  }

  private cancelUnverifiedSignOut(): void {
    if (this.unverifiedSignOutTimer !== null) {
      clearTimeout(this.unverifiedSignOutTimer);
      this.unverifiedSignOutTimer = null;
    }
  }

  private async continueWithVerifiedUser(firebaseUser: FirebaseUser): Promise<boolean> {
    await firebaseUser.reload();

    if (!firebaseUser.emailVerified) {
      const tokenResult = await firebaseUser.getIdTokenResult(true);

      if (tokenResult.claims['email_verified'] !== true) {
        return false;
      }
    }

    this.cancelUnverifiedSignOut();
    this.authState.update(state => ({
      ...state,
      user: this.transformUserToAppModel(firebaseUser),
    }));
    this.waitForClaimsAndStoreToken(firebaseUser);
    return true;
  }

  private async doRefreshToken(): Promise<string | null> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) return null;

    const token = await currentUser.getIdToken(true);
    this.authState.update(state => ({ ...state, authToken: token }));
    return token;
  }

  private async logMarketingConsent(uid: string): Promise<void> {
    await addDoc(collection(this.firestore, 'audit_logs'), {
      action: 'marketing_consent_granted',
      timestamp: serverTimestamp(),
      uid,
      version: '1.0',
    });
  }

  private async logTermsAcceptance(uid: string): Promise<void> {
    await addDoc(collection(this.firestore, 'audit_logs'), {
      action: 'terms_accepted',
      timestamp: serverTimestamp(),
      uid,
      version: '1.0',
    });
  }

  private promptVerificationEmailResend(firebaseUser: FirebaseUser): void {
    if (Date.now() < this.verificationEmailResendAvailableAt) {
      this.snackbarService.open(this.translationStore.translate('COMMON.WEB_API_ERRORS.VERIFICATION_EMAIL_COOLDOWN'));
      this.signOut();
      return;
    }

    this.snackbarService
      .open(
        this.translationStore.translate('AUTH.MESSAGES.VERIFY_YOUR_EMAIL'),
        this.translationStore.translate('AUTH.ACTIONS.RESEND'),
      )
      .onAction()
      .pipe(take(1))
      .subscribe(() => {
        void this.resendVerificationEmail(firebaseUser);
      });

    this.scheduleUnverifiedSignOut();
  }

  private reasonCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse) || !error.error || typeof error.error !== 'object') {
      return null;
    }

    const reasonCode = (error.error as { reasonCode?: unknown }).reasonCode;
    return typeof reasonCode === 'string' ? reasonCode : null;
  }

  private async resendVerificationEmail(firebaseUser: FirebaseUser): Promise<void> {
    this.cancelUnverifiedSignOut();
    this.handlingVerificationEmail = true;

    try {
      if (Date.now() < this.verificationEmailResendAvailableAt) {
        this.snackbarService.open(this.translationStore.translate('COMMON.WEB_API_ERRORS.VERIFICATION_EMAIL_COOLDOWN'));
        this.signOut();
        return;
      }

      const token = await firebaseUser.getIdToken();

      await firstValueFrom(
        this.httpClient.post<void>(`${this.webAppApiBaseUrl}/users/me/verification-email`, null, {
          headers: {
            Authorization: `Bearer ${token}`,
            Language: this.translationStore.currentLanguage(),
          },
        }),
      );

      this.verificationEmailResendAvailableAt = Date.now() + VERIFICATION_EMAIL_RESEND_COOLDOWN_MS;
      this.snackbarService.open(this.translationStore.translate('AUTH.MESSAGES.VERIFICATION_EMAIL_SENT'));
      this.signOut();
    } catch (error) {
      if (this.reasonCode(error) === EMAIL_ALREADY_VERIFIED_REASON) {
        try {
          if (await this.continueWithVerifiedUser(firebaseUser)) {
            this.snackbarService.open(this.translationStore.translate('COMMON.WEB_API_ERRORS.EMAIL_ALREADY_VERIFIED'));
            return;
          }
        } catch (refreshError) {
          this.errorHandler.handleError(refreshError);
          this.signOut();
          return;
        }
      }

      this.errorHandler.handleError(error);
      this.signOut();
    } finally {
      this.handlingVerificationEmail = false;
    }
  }

  private scheduleUnverifiedSignOut(): void {
    this.cancelUnverifiedSignOut();

    this.unverifiedSignOutTimer = setTimeout(() => {
      this.unverifiedSignOutTimer = null;

      const currentUser = this.auth.currentUser;

      if (!this.handlingVerificationEmail && currentUser && !currentUser.emailVerified) {
        this.signOut();
      }
    }, this.snackBarConfig.duration ?? 4000);
  }

  private storeToken(firebaseUser: FirebaseUser): void {
    firebaseUser.getIdToken().then(token => this.authState.update(state => ({ ...state, authToken: token })));
  }

  private transformUserToAppModel(user: FirebaseUser): User {
    return {
      displayName: user.displayName,
      email: user.email,
      photoURL: user.photoURL,
      providerId: user.providerId,
      uid: user.uid
    };
  }

  private waitForClaimsAndStoreToken(firebaseUser: FirebaseUser): void {
    timer(0, 1000)
      .pipe(
        take(10),
        switchMap(() => from(firebaseUser.getIdTokenResult(true))),
        first(({ claims }) => Object.hasOwn(claims, 'rent2endId') || isPlatformServer(this.platformId)),
      )
      .subscribe(({ token }) =>
        this.authState.update(state => ({ ...state, authToken: token }))
      );
  }
}

export const provideFirebaseAuth = (): EnvironmentProviders => makeEnvironmentProviders([
  { provide: AUTH_ADAPTER, useClass: FirebaseAuthAdapter },
]);
